import { Box, Li, Typography, Ul, getTokenCssVar } from '@vp/swan';
import { __ } from '@99designs/i18n';

export interface ReportedIssuesProps {
    reportedIssues?: readonly string[] | null;
}

export function ReportedIssues({ reportedIssues }: ReportedIssuesProps) {
    const messages =
        reportedIssues
            ?.map((issue) => getReportedIssueMessage(issue))
            .filter((message): message is string => Boolean(message)) ?? [];

    if (messages.length === 0) {
        return null;
    }

    return (
        <Box
            mt={4}
            p={4}
            style={{
                backgroundColor: getTokenCssVar('SwanSemColorBgStrong'),
                borderRadius: getTokenCssVar('SwanSemBorderRadiusStandard'),
            }}
            data-testid="brief-product-card-reported-issues"
        >
            <Typography fontSkin="body-standard-bold" mb={2}>
                {__("What we'll fix", {
                    _context:
                        'Header for the section listing the validation issues the designer will fix, found in the design PDF the customer uploaded in the upload-and-print flow before they started this design service brief',
                })}
            </Typography>
            <Typography mb={5}>
                {__('Your designer will fix the errors that are in your file:', {
                    _context:
                        'Lead-in sentence before a bulleted list of validation errors found in the customer’s uploaded design PDF, reassuring them the designer will resolve these issues',
                })}
            </Typography>
            <Ul mb={0}>
                {messages.map((message, index) => (
                    <Li key={`${message}-${index}`}>{message}</Li>
                ))}
            </Ul>
        </Box>
    );
}

function getReportedIssueMessage(issue: string) {
    switch (issue) {
        case 'ImageResolutionValidator':
            return __('Low resolution images are used', {
                _context:
                    'Validation error shown to a customer whose uploaded design PDF is not eligible for printing because it contains low resolution images',
            });
        case 'TemplateMismatchValidator':
            return __('Cover pages aren’t configured correctly', {
                _context:
                    'Validation error shown to a customer whose uploaded design PDF is not eligible for printing. Applies only to Perfect bound and Hard bound booklet products, where the front cover, spine, and back cover must be a single page in the PDF; this error occurs when they are separate pages instead',
            });
        case 'UnEmbeddedFontsValidator':
            return __('Fonts are not embedded', {
                _context:
                    'Validation error shown to a customer whose uploaded design PDF is not eligible for printing because it uses fonts that aren’t embedded in the file',
            });
        case 'PageSizeValidator':
            return __('Page size mismatch', {
                _context:
                    'Validation error shown to a customer whose uploaded design PDF is not eligible for printing because its page size doesn’t match the print product configuration they selected in the upload-and-print flow',
            });
        case 'PageCountValidator':
            return __('Page count mismatch', {
                _context:
                    'Validation error shown to a customer whose uploaded design PDF is not eligible for printing because its page count doesn’t match the print product configuration they selected in the upload-and-print flow',
            });
        case 'PageOrientationValidator':
            return __('Page orientation mismatch', {
                _context:
                    'Validation error shown to a customer whose uploaded design PDF is not eligible for printing because its page orientation doesn’t match the print product configuration they selected in the upload-and-print flow',
            });
        default:
            return null;
    }
}
